import * as libCon from '../community-hats-js-library/Constants';
import * as locCon from '../LocalConstants';
import { RefATOField, RefStoredBooleanValue, setStoredValue } from '../hooks/StoreHooks';
import { RefContextLanguage } from '../providers/LanguageProvider';
import { useCallback, useEffect, useState } from 'react';
import TestGoogleHealthApiButton from './TestGoogleHealthApiButton';
import CustomText from '../elements/CustomText';
import CustomSpin from '../elements/CustomSpin';
import { goToThirdPartyLink, isNullOrUndefined } from '../community-hats-js-library/utils/generalFunctions';
import CustomButton, { CustomPrimaryButtonWithDisability } from '../elements/CustomButton';
import { getGoogleHealthAuthURLForEmail } from '../community-hats-js-library/back-end/router';
import { showErrorDialogByCode } from '../utils/dialogFunctions';
import { showNotification } from '../utils/generalFunctions';
import { SimpleCopyFieldFromATO } from '../elements/SimpleStoreCopyField';
import { SyncATOButton } from '../elements/SyncATOButton';
import { BackButtonLarge } from '../elements/BackButton';

// The Google Health counterpart of FitbitTokenUpdater, stage for stage.
//
// The tokens never touch the browser: the manager consents, the server's
// callback writes the email record, and the test asks the server whether the
// grant works, by email. Nothing here reads a token, which is why the test can
// come before the pull — the pull is only so the manager sees a fresh token
// status before leaving.
//
// Every stage turns into text once it is done, so the one live button on screen
// is always the next thing to press.
export function GoogleHealthTokenUpdater() {
  const i18n = RefContextLanguage();

  const email = RefATOField(locCon.AT_OBJECT_EMAIL_ACCOUNT, libCon.ATF_EMAIL);

  const apiOk = RefStoredBooleanValue(locCon.GOOGLE_HEALTH_API_OK);

  const [authUrl, setAuthUrl] = useState(() => null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [authOpened, setAuthOpened] = useState(false);
  const [hasSynced, setHasSynced] = useState(false);

  // The consent link is fetched here rather than inside the click handler on
  // purpose. goToThirdPartyLink is window.open, and a window.open that happens
  // after an await is no longer part of the user's gesture, so the browser
  // blocks it. In an installed PWA that shows up as a button that does nothing.
  const loadAuthURL = useCallback(async () => {
    if (isNullOrUndefined(email)) return;

    setLoadingUrl(true);
    setAuthUrl(null);

    const [status, url] = await getGoogleHealthAuthURLForEmail(email);

    if (status === libCon.OK) setAuthUrl(url);
    else showErrorDialogByCode(status);

    setLoadingUrl(false);
  }, [email]);

  // The same shape the other Tool pages use. Collapsing the staged reveal is also
  // how a manager gets a fresh consent link: the signed state only lasts ten
  // minutes, and restarting mints a new one rather than making that its own
  // button.
  const restartProcess = () => {
    setStoredValue(locCon.GOOGLE_HEALTH_API_OK, false);
    setAuthOpened(false);
    setHasSynced(false);
    loadAuthURL();

    showNotification(i18n.t('processRestarted'));
  };

  useEffect(() => {
    setStoredValue(locCon.GOOGLE_HEALTH_API_OK, false);
  }, []);

  useEffect(() => {
    loadAuthURL();
  }, [loadAuthURL]);

  return (
    <div className="verticalSection">
      <CustomText type={locCon.SECTION_TITLE_TEXT}>{i18n.t('updateGoogleHealthAPI')}</CustomText>
      <CustomText type={locCon.INSTRUCTIONS_TEXT}>{i18n.t('updateGoogleHealthAPIInstructions')}</CustomText>

      <CustomButton type={locCon.PRIMARY_BUTTON} onClick={() => restartProcess()}>
        {i18n.t('restartProcess')}
      </CustomButton>

      <div className="horizontalLine" />

      <SimpleCopyFieldFromATO
        atoId={locCon.AT_OBJECT_EMAIL_ACCOUNT}
        fieldId={libCon.ATF_EMAIL}
        title={i18n.t('email')}
      />

      <CustomText type={locCon.ELEMENT_TITLE_TEXT}>{i18n.t('goToAuthPage')}</CustomText>

      {loadingUrl ? (
        <CustomSpin type={locCon.BUTTON_SPINNER} />
      ) : authOpened === true ? (
        <CustomText type={locCon.SUCCESS_TEXT}>{i18n.t('appAuthorized')}</CustomText>
      ) : (
        <CustomPrimaryButtonWithDisability
          isDisabled={isNullOrUndefined(authUrl)}
          title={i18n.t('missingValues')}
          description={isNullOrUndefined(email) ? libCon.ATF_EMAIL : i18n.t('error')}
          onClick={() => {
            goToThirdPartyLink(authUrl);
            setAuthOpened(true);
          }}
        >
          {i18n.t('authorizeApp')}
        </CustomPrimaryButtonWithDisability>
      )}

      {authOpened === true ? (
        <div className="verticalSection" style={{ marginTop: '1vh' }}>
          <TestGoogleHealthApiButton />
        </div>
      ) : (
        <div style={{ height: '3vh' }}></div>
      )}

      {apiOk === true ? (
        <div className="verticalSection" style={{ marginTop: '3vh' }}>
          <CustomText type={locCon.ELEMENT_TITLE_TEXT}>{i18n.t('syncEmailAfterAuthorization')}</CustomText>

          {/* becomeTextOnSuccess is hasSynced, not true, because the email object
              is already ATO_UP_TO_DATE when the tool opens on a synced bundle, and
              a plain true renders that state as a static success message the
              manager never pressed — leaving the token status below it as whatever
              was there before the authorization. Tying it to hasSynced keeps the
              button up until the pull actually runs. checkDependencies is off for
              the mirror case: the gate wants all nine NECESSARY_IDS, and a pull
              needs only the email. */}
          <SyncATOButton
            atoId={locCon.AT_OBJECT_EMAIL_ACCOUNT}
            atoBehavior={libCon.ATO_BEHAVIOR_PULL}
            becomeTextOnSuccess={hasSynced}
            checkDependencies={false}
            onClickCleanup={(ok) => {
              if (ok) setHasSynced(true);
            }}
          />
          <SimpleCopyFieldFromATO
            atoId={locCon.AT_OBJECT_EMAIL_ACCOUNT}
            fieldId={libCon.ATF_GOOGLE_TOKEN_STATUS}
            title={i18n.t('googleTokenStatus')}
            enableCopy={false}
          />
        </div>
      ) : (
        <div style={{ height: '3vh' }}></div>
      )}

      {hasSynced === true ? (
        <div className="verticalSection" style={{ marginTop: '1vh' }}>
          <BackButtonLarge />
        </div>
      ) : (
        <div style={{ height: '3vh' }}></div>
      )}
    </div>
  );
}

export default GoogleHealthTokenUpdater;
